/**
 * The re-acceptance prompt (`KLQ-20260814-007` §C): counsel republished something material,
 * and the person has not agreed to it yet.
 *
 * **It is a notice and never a gate**, which is the whole of §C's anti-hostage rule
 * (`MCK-20260521-001`) restated on this side of the wire. There is no route guard, no modal, no
 * overlay and nothing to dismiss: it is drawn at the top of the content pane, the screen behind
 * it is fully usable, and every read, every version history and every export works while it is
 * up. The customer refusing new terms is exactly the customer entitled to get their drawings
 * out, and a client that put a dialog in front of them would take that away no matter how the
 * server answers. What is actually withheld is *starting new work*, and that decision is the
 * server's alone — `legal/deps.py:RequiresCurrentAcceptances` on `POST /api/v1/projects`.
 *
 * **Only `material` is rendered.** The endpoint also reports `initial` — never accepted at all —
 * and that has its own enforcement points at signup and at the claim, which are the two places a
 * first acceptance is actually collected. Drawing it here would put a permanent notice above
 * every fixture-built session with nothing useful to do about it.
 *
 * **The hash comes from the text that was served**, not from the outstanding row: the accept
 * call sends `content_hash` and the endpoint compares it before writing, so a tab left open
 * across a republication meets a `409` and is told to re-read rather than being bound to a
 * version nobody displayed. That is also why the row waits for its document before it offers
 * a button at all.
 */

import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import { Button } from '@/components';
import type { MessageKeys, Translate } from '@/lib/i18n';
import { useLocale, useMessages } from '@/lib/i18n';

import {
  legalDocumentQueryOptions,
  useAcceptLegalDocument,
  useOutstandingAcceptances,
} from './queries';
import styles from './ReAcceptanceNotice.module.css';

/**
 * What each acceptable kind is called in the interface.
 *
 * A key per kind, never a legal text or a title pasted out of the document: the name in the
 * chrome is interface copy a translator owns, and the document's own words are in the file
 * counsel wrote. A kind with no entry here — one added to `PERSONAL_KINDS` before its copy
 * lands — falls back to its wire name rather than rendering an empty row.
 *
 * TWIN: `PERSONAL_KINDS` and `WORKSPACE_KINDS` in `backend/.../legal/models.py`. A kind added
 * to either is a name added here.
 */
const DOCUMENT_NAME_KEYS: Readonly<Record<string, MessageKeys['app']>> = {
  terms: 'reacceptance.document.terms',
  privacy: 'reacceptance.document.privacy',
  avv: 'reacceptance.document.avv',
};

function documentName(t: Translate<'app'>, kind: string): string {
  const key = DOCUMENT_NAME_KEYS[kind];
  return key === undefined ? kind : t(key);
}

export function ReAcceptanceNotice() {
  const t = useMessages('app');
  // No `enabled` guard of its own: this is only ever mounted inside the chrome, and the chrome
  // is not drawn for an unclaimed session (`MCK-20260812-009` §E). So the one request it makes
  // is already conditioned on the only condition worth conditioning it on. The list is asked
  // for the company this tab acts as — the AVV is owed per workspace, so a switch is a
  // different question and gets its own answer.
  const outstanding = useOutstandingAcceptances();

  const material = (outstanding.data ?? []).filter((item) => item.reason === 'material');
  // Nothing owed, still loading, or the read failed: the chrome says nothing. A notice that
  // appeared because a request errored would be a false statement about a legal obligation.
  if (material.length === 0) return null;

  return (
    // `status` and not `alert`: a screen reader should mention this on the way past, the way a
    // sighted reader meets it above the screen, and should not interrupt what somebody is
    // doing. Interrupting is what a modal does, and a modal is the thing §C forbids.
    <section className={styles.notice} role="status">
      <h2 className={styles.title}>{t('reacceptance.title')}</h2>
      <p className={styles.body}>{t('reacceptance.body')}</p>
      <ul className={styles.list}>
        {material.map((item) => (
          <DocumentRow key={item.kind} kind={item.kind} version={item.version} />
        ))}
      </ul>
    </section>
  );
}

/**
 * One document: what it is, where to read it, and the button that agrees to it.
 *
 * A component per row rather than a `useQueries` over the list, because each row owns a
 * document read *and* a mutation of its own — accepting the privacy policy must not put the
 * AVV's button in flight, and a shared mutation would.
 */
function DocumentRow({ kind, version }: { readonly kind: string; readonly version: number }) {
  const t = useMessages('app');
  const { locale } = useLocale();
  // The same cache entry `/legal/$kind` reads, so the hash sent below belongs to the text the
  // link opens. `staleTime` is an hour; the link and the button cost one request between them.
  const document = useQuery(legalDocumentQueryOptions(kind, locale));
  const accept = useAcceptLegalDocument();
  const served = document.data;

  return (
    <li className={styles.row}>
      <div className={styles.identity}>
        <span className={styles.name}>{documentName(t, kind)}</span>
        <span className={styles.version}>{t('reacceptance.version', { version })}</span>
      </div>

      <div className={styles.actions}>
        {/* `target="_blank"` for the reason the signup consent line gives: reading the new
            text must not discard what the person was doing on the screen behind this. */}
        <Link className={styles.read} to="/legal/$kind" params={{ kind }} target="_blank">
          {t('reacceptance.read')}
        </Link>
        <Button
          variant="primary"
          size="sm"
          loading={accept.isPending}
          disabled={served === undefined}
          onClick={() => {
            if (served === undefined) return;
            accept.mutate(
              {
                kind,
                // The language the document was *served* in, which the fallback makes
                // different from the one asked for: the AVV is German whatever the interface
                // says.
                locale: served.locale,
                contentHash: served.content_hash,
              },
              {
                onError: (error) => {
                  // A `409` means the text was republished while this tab held it. The cached
                  // copy is now the wrong one, so it is re-read: without this the link opens
                  // yesterday's version and every retry sends yesterday's hash, which is the
                  // same refusal forever.
                  if (error instanceof ApiError && error.status === 409) void document.refetch();
                },
              },
            );
          }}
        >
          {t('reacceptance.accept')}
        </Button>
      </div>

      {document.isError ? <p className={styles.problem}>{t('reacceptance.unavailable')}</p> : null}
      {accept.isError ? (
        <p className={styles.problem}>
          {accept.error instanceof ApiError && accept.error.status === 409
            ? t('reacceptance.stale')
            : t('reacceptance.failed')}
        </p>
      ) : null}
    </li>
  );
}
